import { useState, useMemo } from 'react';
import {
  FileText, Search, Download, Eye, CheckCircle2, Clock, Filter,
  ChevronDown, File, Sheet, Printer, Calendar, Ship, AlertTriangle,
  XCircle, EyeOff
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { mockFleets, mockVessels } from '../../data/mockData';
import { formatDate, getInspectionStatusLabel, getInspectionStatusColor } from '../../utils/helpers';

type ExportFormat = 'pdf' | 'excel';

export default function ReportsPage() {
  const { inspections } = useAppStore();

  // Filters
  const [search, setSearch] = useState('');
  const [selectedFleet, setSelectedFleet] = useState('all');
  const [selectedVessel, setSelectedVessel] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('pdf');
  const [showExportModal, setShowExportModal] = useState<string | null>(null);

  // Get vessels for selected fleet
  const filteredVessels = useMemo(() => {
    if (selectedFleet === 'all') return mockVessels;
    return mockVessels.filter(v => v.fleetId === selectedFleet);
  }, [selectedFleet]);

  // Filter inspections
  const filteredInspections = useMemo(() => {
    return inspections.filter(insp => {
      // Search filter
      if (search) {
        const s = search.toLowerCase();
        if (!insp.vesselName.toLowerCase().includes(s) && !insp.questionnaireName.toLowerCase().includes(s)) return false;
      }
      // Fleet filter (via vessel's fleetId)
      if (selectedFleet !== 'all') {
        const vessel = mockVessels.find(v => v.id === insp.vesselId);
        if (!vessel || vessel.fleetId !== selectedFleet) return false;
      }
      // Vessel filter
      if (selectedVessel !== 'all' && insp.vesselId !== selectedVessel) return false;
      // Status filter
      if (selectedStatus !== 'all' && insp.status !== selectedStatus) return false;
      // Period filter
      if (selectedPeriod !== 'all') {
        const inspDate = new Date(insp.completedDate || insp.scheduledDate);
        const now = new Date();
        if (selectedPeriod === 'last7' && (now.getTime() - inspDate.getTime()) > 7 * 24 * 60 * 60 * 1000) return false;
        if (selectedPeriod === 'last30' && (now.getTime() - inspDate.getTime()) > 30 * 24 * 60 * 60 * 1000) return false;
        if (selectedPeriod === 'last90' && (now.getTime() - inspDate.getTime()) > 90 * 24 * 60 * 60 * 1000) return false;
        if (selectedPeriod === 'lastYear' && (now.getTime() - inspDate.getTime()) > 365 * 24 * 60 * 60 * 1000) return false;
      }
      return true;
    });
  }, [inspections, search, selectedFleet, selectedVessel, selectedStatus, selectedPeriod]);

  // Stats
  const totalReports = filteredInspections.length;
  const approvedCount = filteredInspections.filter(i => i.status === 'approved').length;
  const pendingCount = filteredInspections.filter(i => i.status === 'completed' || i.status === 'under_review').length;

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Handle download
  const handleDownload = (inspectionId: string, format: ExportFormat) => {
    const insp = inspections.find(i => i.id === inspectionId);
    if (!insp) return;

    if (format === 'pdf') {
      // Generate PDF content
      const content = generatePDFContent(insp);
      const blob = new Blob([content], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RISQ_Report_${insp.vesselName.replace(/\s+/g, '_')}_${insp.id}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // Generate Excel/CSV content
      const content = generateExcelContent(insp);
      const blob = new Blob([content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RISQ_Report_${insp.vesselName.replace(/\s+/g, '_')}_${insp.id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    setShowExportModal(null);
  };

  // Generate PDF content (HTML-based)
  const generatePDFContent = (insp: typeof inspections[0]) => {
    return `<!DOCTYPE html>
<html>
<head>
  <title>RISQ 3.2 Report - ${insp.vesselName}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
    h1 { color: #0A1628; border-bottom: 2px solid #0EA5E9; padding-bottom: 10px; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; }
    .meta-item { background: #f8fafc; padding: 12px; border-radius: 8px; }
    .meta-label { font-size: 11px; color: #64748b; text-transform: uppercase; }
    .meta-value { font-size: 14px; font-weight: 600; color: #1e293b; }
    .score { font-size: 24px; font-weight: bold; color: ${insp.overallScore && insp.overallScore >= 7 ? '#10b981' : insp.overallScore && insp.overallScore >= 5 ? '#f59e0b' : '#ef4444'}; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; }
    th { background: #0A1628; color: white; }
    .footer { margin-top: 40px; font-size: 11px; color: #94a3b8; text-align: center; }
  </style>
</head>
<body>
  <h1>RISQ 3.2 Inspection Report</h1>
  <div class="meta">
    <div class="meta-item"><div class="meta-label">Vessel</div><div class="meta-value">${insp.vesselName}</div></div>
    <div class="meta-item"><div class="meta-label">Inspector</div><div class="meta-value">${insp.inspectorName}</div></div>
    <div class="meta-item"><div class="meta-label">Questionnaire</div><div class="meta-value">${insp.questionnaireName}</div></div>
    <div class="meta-item"><div class="meta-label">Type</div><div class="meta-value">${insp.type}</div></div>
    <div class="meta-item"><div class="meta-label">Status</div><div class="meta-value">${getInspectionStatusLabel(insp.status)}</div></div>
    <div class="meta-item"><div class="meta-label">Overall Score</div><div class="meta-value score">${insp.overallScore || 'N/A'}</div></div>
  </div>
  <h2>Summary</h2>
  <table>
    <tr><th>Metric</th><th>Value</th></tr>
    <tr><td>Total Questions</td><td>${insp.totalQuestions}</td></tr>
    <tr><td>Answered</td><td>${insp.answeredQuestions}</td></tr>
    <tr><td>Passed</td><td>${insp.passCount}</td></tr>
    <tr><td>Failed</td><td>${insp.failCount}</td></tr>
    <tr><td>N/A</td><td>${insp.naCount}</td></tr>
    <tr><td>Risk Rating</td><td>${insp.riskRating || 'N/A'}</td></tr>
  </table>
  ${insp.notes ? `<h2>Notes</h2><p>${insp.notes}</p>` : ''}
  <div class="footer">Generated on ${new Date().toLocaleDateString()} | Vessel Navigator - Maritime Inspection Platform</div>
</body>
</html>`;
  };

  // Generate Excel/CSV content
  const generateExcelContent = (insp: typeof inspections[0]) => {
    const rows = [
      ['Field', 'Value'],
      ['Report ID', insp.id],
      ['Vessel Name', insp.vesselName],
      ['Inspector', insp.inspectorName],
      ['Questionnaire', insp.questionnaireName],
      ['Inspection Type', insp.type],
      ['Status', getInspectionStatusLabel(insp.status)],
      ['Overall Score', insp.overallScore?.toString() || 'N/A'],
      ['Risk Rating', insp.riskRating || 'N/A'],
      ['Total Questions', insp.totalQuestions.toString()],
      ['Answered Questions', insp.answeredQuestions.toString()],
      ['Passed', insp.passCount.toString()],
      ['Failed', insp.failCount.toString()],
      ['N/A', insp.naCount.toString()],
      ['Scheduled Date', insp.scheduledDate],
      ['Started Date', insp.startedDate || 'N/A'],
      ['Completed Date', insp.completedDate || 'N/A'],
      ['Approved Date', insp.approvedDate || 'N/A'],
      ['Notes', insp.notes || ''],
    ];
    return rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'approved', label: 'Approved' },
    { value: 'completed', label: 'Completed' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'draft', label: 'Draft' },
  ];

  const periodOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'last7', label: 'Last 7 Days' },
    { value: 'last30', label: 'Last 30 Days' },
    { value: 'last90', label: 'Last 90 Days' },
    { value: 'lastYear', label: 'Last Year' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Inspection Reports</h1>
          <p className="text-sm text-slate-500 mt-1">{totalReports} reports found</p>
        </div>
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2 shadow-md shadow-sky-500/20"
        >
          <Printer className="w-4 h-4" /> Print All
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-50 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalReports}</p>
              <p className="text-xs text-slate-500">Total Reports</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{approvedCount}</p>
              <p className="text-xs text-slate-500">Approved</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{pendingCount}</p>
              <p className="text-xs text-slate-500">Pending Review</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Fleet Filter */}
          <div className="relative">
            <Ship className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedFleet}
              onChange={e => { setSelectedFleet(e.target.value); setSelectedVessel('all'); }}
              className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none cursor-pointer"
            >
              <option value="all">All Fleets</option>
              {mockFleets.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Vessel Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedVessel}
              onChange={e => setSelectedVessel(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none cursor-pointer"
            >
              <option value="all">All Vessels</option>
              {filteredVessels.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Period Filter */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedPeriod}
              onChange={e => setSelectedPeriod(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none cursor-pointer"
            >
              {periodOptions.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <AlertTriangle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none cursor-pointer"
            >
              {statusOptions.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search reports..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {filteredInspections.length === 0 ? (
          <div className="p-12 text-center">
            <EyeOff className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No reports match your filters</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs">Vessel</th>
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden sm:table-cell">Questionnaire</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs hidden lg:table-cell">Score</th>
                <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Pass/Fail</th>
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden lg:table-cell">Status</th>
                <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Completed</th>
                <th className="text-right px-5 py-3 font-medium text-slate-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInspections.map(inspection => (
                <tr key={inspection.id}
                  className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-sky-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Ship className="w-5 h-5 text-sky-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{inspection.vesselName}</p>
                        <p className="text-xs text-slate-400">{inspection.inspectorName}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <p className="text-xs text-slate-600">{inspection.questionnaireName}</p>
                    <p className="text-[10px] text-slate-400 capitalize">{inspection.type}</p>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell text-center">
                    <span className={`text-lg font-bold ${
                      inspection.overallScore && inspection.overallScore >= 7 ? 'text-emerald-600' :
                      inspection.overallScore && inspection.overallScore >= 5 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {inspection.overallScore || '—'}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-center">
                    <div className="flex items-center justify-center gap-2 text-xs">
                      <span className="text-emerald-600 font-medium">{inspection.passCount}P</span>
                      <span className="text-slate-300">/</span>
                      <span className="text-red-600 font-medium">{inspection.failCount}F</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getInspectionStatusColor(inspection.status)}`}>
                      {getInspectionStatusLabel(inspection.status)}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="text-xs text-slate-500">
                      {formatDate(inspection.completedDate || inspection.scheduledDate)}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setShowExportModal(inspection.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 text-sky-700 rounded-lg text-xs font-medium hover:bg-sky-100 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Download
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowExportModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-1">Export Report</h3>
            <p className="text-sm text-slate-500 mb-5">Choose your preferred format</p>

            <div className="space-y-3">
              <button
                onClick={() => handleDownload(showExportModal, 'pdf')}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-red-400 hover:bg-red-50 transition-all group"
              >
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center group-hover:bg-red-200 transition-colors">
                  <File className="w-6 h-6 text-red-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-800">PDF Report</p>
                  <p className="text-xs text-slate-500">Full branded report with formatting</p>
                </div>
              </button>

              <button
                onClick={() => handleDownload(showExportModal, 'excel')}
                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all group"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <Sheet className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-slate-800">Excel / CSV</p>
                  <p className="text-xs text-slate-500">Spreadsheet data for analysis</p>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowExportModal(null)}
              className="w-full mt-4 px-4 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
